import * as Sentry from "@sentry/react";
import * as Ably from "ably";
import { AblyProvider } from "ably/react";
import { useEffect, useState } from "react";
import { Button } from "semantic-ui-react";
import AppRoutes from "./Routes";
import { ablyToken } from "./api/auth";
import NeedChrome from "./components/NeedChrome";
import SplashScreen from "./components/SplashScreen";
import config from "./config";
import { useAuth } from "./contexts/AuthContext";

const ablyClient = Ably.Realtime.Promise({
  authCallback: async (data, callback) => {
    try {
      const tokenRequest = await ablyToken();
      callback(null, tokenRequest.data);
    } catch (e) {
      callback(e, null);
    }
  },
});

Sentry.init({
  dsn: config.sentry_dsn || undefined,
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
      networkDetailAllowUrls: [
        config.api_url,
        ...config.network_detail_allowed_urls,
      ],
      // response has a host login ticket (host_url)
      networkDetailDenyUrls: [`${config.api_url}/primum/meetings`],
    }),
  ],
  environment: config.sentry_env,
  sendDefaultPii: true,
  replaysSessionSampleRate: 0.4,
  replaysOnErrorSampleRate: 1.0,
  ignoreErrors: ["needs authentication", "E_CALL_GET_USER_INFO"],
});

function App() {
  const { apiUser } = useAuth();
  const [isChrome, setIsChrome] = useState();
  const [continueAnyway, setContinueAnyway] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const _isChrome = !!window.chrome;
    setIsChrome(_isChrome);
    if (_isChrome) {
      Notification.requestPermission();
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (apiUser) {
      Sentry.setUser({
        id: apiUser.id,
        email: apiUser.email,
        username: `${apiUser.first_name} ${apiUser.last_name}`,
      });
    }
  }, [apiUser]);

  if (loading) return <SplashScreen />;

  return isChrome || continueAnyway ? (
    <AblyWrapper>
      <AppRoutes />
    </AblyWrapper>
  ) : (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <NeedChrome />
      <Button primary onClick={() => setContinueAnyway(true)}>
        Continue Anyway
      </Button>
    </div>
  );
}

function AblyWrapper({ children }) {
  const { apiUser } = useAuth();

  if (!apiUser?.has_ably) return children;

  return <AblyProvider client={ablyClient}>{children}</AblyProvider>;
}

export default App;
